import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DeleteIconButton } from '../../../components/delete-icon-button';
import { DelayedLoadIndicator } from '../../../components/delayed-load-indicator';
import { Timestamp } from '../../../components/timestamp';
import { TrustedPublisherProviderIcon } from './trusted-publisher-provider-icon';
import { findRegistrationPathPair, orderRegistrationKeys } from './registration-fields';
const PublisherRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2)
}));
// e.g. "octocat/my-repo · workflow: release.yml · environment: prod"
const registrationSummary = (registration) => {
    const reg = registration ?? {};
    const keys = orderRegistrationKeys(Object.keys(reg)).filter(key => reg[key]?.trim());
    const pair = findRegistrationPathPair(keys);
    const path = pair?.map(key => reg[key]).join('/');
    const fields = keys.filter(key => !pair?.includes(key)).map(key => `${key}: ${reg[key]}`);
    return [path, ...fields].filter(Boolean).join(' · ');
};
export const PublisherList = props => {
    const rowDetail = props.rowDetail ?? 'extension';
    const [deleteTarget, setDeleteTarget] = useState();
    const providerName = (id) => props.providers.find(p => p.id === id)?.name ?? id;
    const rowDetailLabel = (publisher) => {
        switch (rowDetail) {
            case 'namespace/extension':
                return `${publisher.namespace}/${publisher.extension}`;
            case 'extension':
                return publisher.extension;
            default:
                return undefined;
        }
    };
    const confirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        await props.onDelete(deleteTarget);
        setDeleteTarget(undefined);
    };
    return (_jsxs(_Fragment, { children: [_jsx(DelayedLoadIndicator, { loading: props.loading }), props.publishers.length ? (_jsx(Paper, { variant: 'outlined', children: _jsx(Stack, { divider: _jsx(Divider, {}), children: props.publishers.map(publisher => {
                        const summary = registrationSummary(publisher.registration);
                        const detail = rowDetailLabel(publisher);
                        return (_jsxs(PublisherRow, { children: [_jsx(TrustedPublisherProviderIcon, { providerId: publisher.provider, fontSize: 'large', sx: { color: 'text.secondary' } }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Typography, { sx: { fontWeight: 'bold' }, children: [providerName(publisher.provider), detail ? _jsxs(_Fragment, { children: [" \u00B7 ", detail] }) : null] }), summary ? (_jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { wordBreak: 'break-word' }, children: summary })) : null, publisher.createdTimestamp ? (_jsxs(Typography, { variant: 'body2', color: 'text.secondary', children: ["Created: ", _jsx(Timestamp, { value: publisher.createdTimestamp })] })) : null] }), _jsx(DeleteIconButton, { onClick: () => setDeleteTarget(publisher), disabled: props.loading, "aria-label": 'Delete trusted publisher' })] }, 'tp-' + publisher.id));
                    }) }) })) : props.emptyText ? (_jsx(Typography, { variant: 'body1', children: props.emptyText })) : null, _jsxs(Dialog, { open: Boolean(deleteTarget), onClose: () => setDeleteTarget(undefined), children: [_jsx(DialogTitle, { children: "Delete trusted publisher" }), _jsx(DialogContent, { children: _jsx(DialogContentText, { component: 'div', children: "Are you sure you want to delete this trusted publisher? Workflows relying on it will no longer be able to publish." }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setDeleteTarget(undefined), children: "Cancel" }), _jsx(Button, { variant: 'contained', color: 'error', autoFocus: true, onClick: confirmDelete, children: "Delete" })] })] })] }));
};
//# sourceMappingURL=publisher-list.js.map